import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	Skeleton,
} from '@mui/material';
import * as React from 'react';
import {
	CerebroComplexListItem,
	CerebroDrawer,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroMaskedInput,
	CerebroIcon,
	DebtCapitalHeading,
} from '.';

import { ReactComponent as OwnerOccupiedRealEstate } from '../icons/OwnerOccupiedRealEstate.svg';
import { useSnackbar } from 'notistack';
import { Field, Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import NumberFormat from 'react-number-format';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../context';
import { getApiResponseData } from '../utils/axios';
import { DealRequestAllocationRealEstate, ProfileRealEstateType, ProfileRealEstateTypeMap } from '../models';
import { HtmlUtils, NumberUtils } from '../utils';
import { QueryKeys, useDealRequestAllocationRealEstatesQuery } from '../hooks';
import { DebtCapitalSectionProps } from '../../pages';
import { CerebroApiEndpoints } from '../utils/api';
import { CerebroForm } from './CerebroForm';
import { MaxCurrencyLength } from '../utils/platform';

export const CerebroDebtRealEstate: React.FC<DebtCapitalSectionProps> = ({ drawerOpenByDefault = false }) => {
	const [open, setOpen] = React.useState(drawerOpenByDefault);
	const [confirmDelete, setConfirmDelete] = React.useState(false);
	const [deleteGuid, setDeleteGuid] = React.useState('');
	const [realEstateRecord, setRealEstateRecord] = React.useState<DealRequestAllocationRealEstateValues>({});
	const api = useApi();
	const { guid } = useParams<'guid'>();
	const realEstateQuery = useDealRequestAllocationRealEstatesQuery(guid);
	const client = useQueryClient();
	const snackbar = useSnackbar();

	const { mutate } = useMutation(
		async (values: DealRequestAllocationRealEstateValues) => {
			var payload: Partial<DealRequestAllocationRealEstate> = {
				realEstateType: values.realEstateType ?? 'Office',
				termLoanNeeds: NumberUtils.ParseDecimal(values.termLoanNeeds),
				cashEquity: NumberUtils.ParseDecimal(values.cashEquity),
			};

			if (!values.guid) {
				const { data, error } = await getApiResponseData<DealRequestAllocationRealEstate>(
					api.post(CerebroApiEndpoints.AllocationRealEstates.Root(guid), payload)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			const { data, error } = await getApiResponseData<DealRequestAllocationRealEstate>(
				api.patch(CerebroApiEndpoints.AllocationRealEstates.Specific(guid, values.guid), payload)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.AllocationRealEstates(guid));

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				setOpen(false);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	const onCreateRecord = () => {
		setRealEstateRecord({
			realEstateType: 'None',
			termLoanNeeds: '0',
			cashEquity: '0',
			totalRealEstateAmount: '0',
		});
		setOpen(true);
	};

	const onEditRecord = (realEstate: DealRequestAllocationRealEstate) => {
		setRealEstateRecord({
			guid: realEstate.guid,
			realEstateType: realEstate.realEstateType,
			termLoanNeeds: (realEstate.termLoanNeeds ?? 0).toString(),
			cashEquity: (realEstate.cashEquity ?? 0).toString(),
			totalRealEstateAmount: ((realEstate.termLoanNeeds ?? 0) + (realEstate.revolvingDebtNeeds ?? 0)).toString(),
		});
		setOpen(true);
	};

	const onDeleteRecord = (realEstate: DealRequestAllocationRealEstate) => {
		if (!realEstate.guid) {
			return;
		}

		setDeleteGuid(realEstate.guid);
		setConfirmDelete(true);
	};

	const onConfirmDeleteClose = () => {
		setDeleteGuid('');
		setConfirmDelete(false);
	};

	const deleteRealEstateRecord = () => {
		api.delete(CerebroApiEndpoints.AllocationRealEstates.Specific(guid, deleteGuid))
			.then(() => {
				client.invalidateQueries(QueryKeys.AllocationRealEstates(guid));

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Real estate detail has been deleted.', { variant: 'success' });
				onConfirmDeleteClose();
			})
			.catch(() => {
				snackbar.enqueueSnackbar('There was a problem deleting the real estate detail.', { variant: 'error' });
				onConfirmDeleteClose();
			});
	};

	const drawerRef = React.useRef<HTMLDivElement>(null);

	if (realEstateQuery.isLoading) {
		return <Skeleton height={400} />;
	}

	const totalCount = realEstateQuery.data?.length ?? 0;

	const amountRequested = NumberUtils.Sum(
		...(realEstateQuery.data?.map(
			realEstate => (realEstate?.termLoanNeeds ?? 0) + (realEstate?.revolvingDebtNeeds ?? 0)
		) ?? [])
	);

	return (
		<>
			<DebtCapitalHeading icon={OwnerOccupiedRealEstate} amountRequested={amountRequested}>
				Owner-Occupied Real Estate
			</DebtCapitalHeading>

			{totalCount === 0 ? (
				<>
					<CerebroStyledBox sx={{ marginBottom: 8 }}>
						<CerebroHeading size="h2" component="h2">
							No details have been added yet.
						</CerebroHeading>
						<br />
						<br />
						<CerebroPrimaryButton wide onClick={() => onCreateRecord()}>
							Add Owner-Occupied Real Estate Details
						</CerebroPrimaryButton>
						<br />
					</CerebroStyledBox>
				</>
			) : (
				<></>
			)}

			{totalCount > 0 ? (
				<>
					{realEstateQuery.data?.map((item: DealRequestAllocationRealEstate) => (
						<CerebroComplexListItem
							key={item.guid}
							sx={{ marginTop: 2 }}
							onEdit={() => onEditRecord(item)}
							onDelete={() => onDeleteRecord(item)}
							onDeleteTitle={`Delete ${item.realEstateType}`}
							showDelete={true}
						>
							<Grid container>
								<Grid item xs={4}>
									<CerebroLabel>Category</CerebroLabel>
									<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
										{item.realEstateType}
									</CerebroText>
								</Grid>
								<Grid item xs={4}>
									<CerebroLabel>Requested Amount</CerebroLabel>
									<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
										<NumberFormat
											value={(item.termLoanNeeds ?? 0) + (item.revolvingDebtNeeds ?? 0)}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</CerebroText>
								</Grid>
								<Grid item xs={4}>
									<CerebroLabel>Equity</CerebroLabel>
									<CerebroText variant="small" sx={{ marginTop: 1.5 }}>
										<NumberFormat
											value={item.cashEquity}
											displayType="text"
											thousandSeparator={true}
											prefix="$"
										/>
									</CerebroText>
								</Grid>
							</Grid>
						</CerebroComplexListItem>
					))}

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							marginTop: 3,
							marginBottom: 8,
						}}
					>
						<CerebroPrimaryButton wide onClick={() => onCreateRecord()}>
							Add an Additional Real Estate Request
							<CerebroIcon padLeft>
								<FontAwesomeIcon icon={faPlus} size="sm" />
							</CerebroIcon>
						</CerebroPrimaryButton>
					</Box>
				</>
			) : (
				<></>
			)}

			<Dialog open={confirmDelete} onClose={onConfirmDeleteClose}>
				<DialogTitle>{'Confirm Delete'}</DialogTitle>
				<DialogContent>
					<DialogContentText>Are you sure you want to delete this real estate detail?</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button autoFocus onClick={onConfirmDeleteClose}>
						No
					</Button>
					<Button onClick={deleteRealEstateRecord} autoFocus>
						Yes
					</Button>
				</DialogActions>
			</Dialog>

			<CerebroDrawer ref={drawerRef} open={open} onClose={() => setOpen(false)} icon={OwnerOccupiedRealEstate}>
				<CerebroHeading>Owner-Occupied Real Estate</CerebroHeading>
				<br />
				<Formik
					initialValues={{
						guid: realEstateRecord.guid,
						realEstateType: realEstateRecord.realEstateType,
						termLoanNeeds: NumberUtils.FormatCurrency(realEstateRecord.termLoanNeeds),
						cashEquity: NumberUtils.FormatCurrency(realEstateRecord.cashEquity),
						totalRealEstateAmount: NumberUtils.FormatCurrency(realEstateRecord.totalRealEstateAmount),
					}}
					validate={validateForm}
					onSubmit={values => mutate(values)}
				>
					{({ isSubmitting, errors, touched, handleSubmit, values, initialValues }) => {
						const error = errors.termLoanNeeds !== undefined && touched.termLoanNeeds;

						return (
							<CerebroForm
								flatIndicator
								onSubmit={handleSubmit}
								scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(drawerRef.current)}
							>
								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel
										required
										htmlFor="realEstateType"
										error={errors.realEstateType !== undefined && touched.realEstateType}
									>
										Which of the following best describes the type of real estate you are
										purchasing?
									</CerebroLabel>

									<Field
										as={CerebroDropdown}
										id="realEstateType"
										name="realEstateType"
										disabled={isSubmitting}
										value={values.realEstateType}
										error={errors.realEstateType !== undefined && touched.realEstateType}
									>
										{Object.entries(ProfileRealEstateTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>

									{touched.realEstateType && errors.realEstateType != null && (
										<CerebroText variant="error">{errors.realEstateType}</CerebroText>
									)}
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel required htmlFor="termLoanNeeds" error={error}>
										Term Loan Needs
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="termLoanNeeds"
										name="termLoanNeeds"
										disabled={isSubmitting}
										value={values.termLoanNeeds}
										error={error}
									/>
								</Box>

								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel htmlFor="cashEquity">Cash Equity</CerebroLabel>
									<CerebroText variant="hint" gutterBottom>
										Lenders typically do not finance beyond 85% of the value of the real estate.
									</CerebroText>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="cashEquity"
										name="cashEquity"
										disabled={isSubmitting}
										value={values.cashEquity}
									/>
								</Box>

								{error && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.termLoanNeeds}
									</CerebroText>
								)}

								<Grid container justifyContent="space-between" sx={{ marginTop: 5 }}>
									<Grid item>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Add Details
										</CerebroPrimaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			</CerebroDrawer>
		</>
	);
};

interface DealRequestAllocationRealEstateValues {
	guid?: string;
	realEstateType?: ProfileRealEstateType;
	termLoanNeeds?: string;
	revolvingDebtNeeds?: string;
	cashEquity?: string;
	totalRealEstateAmount?: string;
}

interface DealRequestAllocationRealEstateValueErrors {
	guid?: string;
	realEstateType?: string;
	termLoanNeeds?: string;
	cashEquity?: string;
	totalRealEstateAmount?: string;
}

function validateForm(values: DealRequestAllocationRealEstateValues) {
	const errors: DealRequestAllocationRealEstateValueErrors = {};

	if (!values.realEstateType || values.realEstateType === 'None') {
		errors.realEstateType = 'Real estate type is required!';
	}

	if (!values.termLoanNeeds && !values.revolvingDebtNeeds) {
		errors.termLoanNeeds = 'Either term loan or revolver need an amount!';
	}

	return errors;
}
